import { useEffect, useState } from 'react'
import { ActionIcon, Alert, Group, Loader, Paper, Stack, Text, ThemeIcon, Title, Tooltip } from '@mantine/core'
import { IconAlertTriangle, IconCircleCheck, IconCircleX, IconHelpCircle, IconRefresh } from '@tabler/icons-react'
import type { EstadoDaParte } from '@/api/whatsappGateways'
import { useSaudeDoGateway } from '@/hooks/useWhatsappGateways'
import { mensagemDeErro } from './mensagens'

const ICONE: Record<EstadoDaParte, { cor: string; icone: typeof IconCircleCheck; rotulo: string }> = {
  ok: { cor: 'green', icone: IconCircleCheck, rotulo: 'Em ordem' },
  alerta: { cor: 'yellow', icone: IconAlertTriangle, rotulo: 'Atenção' },
  erro: { cor: 'red', icone: IconCircleX, rotulo: 'Com problema' },
  desconhecido: { cor: 'gray', icone: IconHelpCircle, rotulo: 'Não conferido' },
}

/** "conferido há 12 s" / "há 3 min": a idade da conferência, que o servidor guarda por 30 s. */
export function idadeDaConferencia(verificadoEm: string, agora: number): string {
  const s = Math.max(0, Math.round((agora - new Date(verificadoEm).getTime()) / 1000))
  return s < 60 ? `conferido há ${s} s` : `conferido há ${Math.floor(s / 60)} min`
}

/**
 * "Estado atual" do gateway (#25885): o que está valendo AGORA, conferido parte por parte
 * (registro no WhatsApi, serviço no ECS, versão, sessão, chaves e bot) — e não o último job.
 * Cada parte traz o próprio estado; a que não pôde ser conferida aparece como "Não conferido",
 * sem esconder as outras.
 */
export function EstadoAtualDoGateway({ orgId, gwId, podeParear }: { orgId: number; gwId: string; podeParear: boolean }) {
  const saude = useSaudeDoGateway(orgId, gwId)
  const [agora, setAgora] = useState(() => Date.now())
  useEffect(() => {
    const t = setInterval(() => setAgora(Date.now()), 5_000)
    return () => clearInterval(t)
  }, [])

  return (
    <Paper withBorder p="sm" radius="md">
      <Stack gap="xs">
        <Group justify="space-between" wrap="nowrap">
          <Title order={5}>Estado atual</Title>
          <Group gap="xs" wrap="nowrap">
            {saude.data && (
              <Text size="xs" c="dimmed">{idadeDaConferencia(saude.data.verificado_em, Math.max(agora, saude.dataUpdatedAt))}</Text>
            )}
            <Tooltip label="Conferir de novo (o servidor guarda a conferência por 30 s)">
              <ActionIcon variant="subtle" aria-label="Conferir de novo" loading={saude.isFetching} onClick={() => void saude.refetch()}>
                <IconRefresh size={16} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Group>
        {saude.isError && <Alert color="red" variant="light">{mensagemDeErro(saude.error)}</Alert>}
        {saude.isLoading && <Loader size="sm" />}
        {saude.data?.itens.map((item) => {
          const v = ICONE[item.estado]
          const Icone = v.icone
          return (
            <Group key={item.chave} gap="sm" wrap="nowrap" align="flex-start" data-estado={item.estado}>
              <ThemeIcon color={v.cor} variant="light" size="md" radius="xl" aria-label={v.rotulo} title={v.rotulo}>
                <Icone size={16} />
              </ThemeIcon>
              <Stack gap={0}>
                <Text size="sm" fw={600}>{item.titulo}</Text>
                <Text size="sm" c="dimmed">{item.detalhe}</Text>
                {item.chave === 'sessao' && item.estado === 'erro' && (
                  <Text size="sm">
                    {podeParear
                      ? 'Para parear, gere o QR em “Pareamento”, abaixo, e leia pelo WhatsApp do número.'
                      : 'Peça a quem tem a permissão de parear para gerar o QR deste gateway.'}
                  </Text>
                )}
              </Stack>
            </Group>
          )
        })}
      </Stack>
    </Paper>
  )
}
